import { SelectUserComponent } from './../../select-user/select-user.component';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateTimeAdapter } from '@danielmoncada/angular-datetime-picker';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Company } from 'src/app/app/company/company.model';
import { CompanyService } from 'src/app/app/company/company.service';
import { customValidators } from 'src/app/_helpers/customValidators';
import { SpaceAssignmentModel } from 'src/app/_models/space-assignment.model';
import { UserBasic } from 'src/app/_models/user.model';
import { UserService } from 'src/app/_services';
import { ThisPlatform } from 'src/app/_services/platform.service';
import { SpaceService } from 'src/app/_services/space.service';
import {
  CompanyGroup,
  CompanyGroupType,
} from '../../../company/group/gorup.model';
import { CompanyGroupService } from '../../../company/group/group-service';

@Component({
  selector: 'app-space-assignment-edit',
  templateUrl: './space-assignment-edit.component.html',
  styleUrls: ['./space-assignment-edit.component.scss'],
})
export class SpaceAssignmentEditComponent implements OnInit {
  constructor(
    public companyServ: CompanyService,
    public groupServ: CompanyGroupService,
    public userServ: UserService,
    private modalController: ModalController,
    private dateTimeAdapter: DateTimeAdapter<any>,
    private spaceServ: SpaceService,
    public p: ThisPlatform,
    public translateServ: TranslateService,
    private alertController: AlertController
  ) {}

  form: FormGroup;
  usersList: UserBasic[] = null;
  value: any;
  validationError: boolean = false;

  startDate: Date = new Date();
  endDate: Date = new Date();
  showLoader: boolean = false;
  savingAssignment: boolean = false;
  dropdownData: Company[];
  groupDropdownData: CompanyGroup[];
  firstCompanySelect = true;

  checkDate: boolean = false;

  allCompanies: Company = {
    ...new Company(),
    id: null,
    name: this.translateServ.instant('ALL'),
  };

  allUsers: UserBasic = {
    ...new UserBasic(),
    id: null,
    email: this.translateServ.instant('SPACE.All_EMPLOYEES'),
  };

  allGroups: CompanyGroup = {
    ...new CompanyGroup(),
    id: null,
    name: this.translateServ.instant('ALL'),
  };

  @Input()
  spaceId: string;

  @Input()
  assignmentId: number;

  @Input()
  headerText: string;

  @Input()
  showUserDropdown: boolean;

  @Input()
  showRegistrationNumberInput: boolean;

  @ViewChild('userSelect') userSelect: SelectUserComponent;

  label = this.translateServ.instant('SELECT_USER');
  public isUserSelected: boolean = false;

  permissionAllCompanyEdit: boolean = false;

  async ngOnInit() {
    this.showLoader = true;
    await this.companyServ.loadCompaniesSync('true');

    this.dropdownData = [this.allCompanies, ...this.companyServ.companies];

    this.dateTimeAdapter.setLocale(this.userServ.getDefaultLanguage());

    this.initForm();

    this.permissionAllCompanyEdit = await this.userServ.permissionEditCompanyAsync(
      this.userServ.userStorage.idCompany,
      ['space.assignment.add'],
      ['space.assignment.add.all-companies']
    );

    if (this.assignmentId) {
      this.spaceServ.getAssignment(this.assignmentId).subscribe(
        (spaceRes: SpaceAssignmentModel) => {
          this.loadAssignmentDetails(spaceRes);
          if (spaceRes.idCompany) {
            this.loadCompanyGroup(spaceRes.idCompany);
            this.loadCompanyUsers(spaceRes.idCompany, spaceRes.idGroup);
          }
        },
        (error) => {},
        () => (this.showLoader = false)
      );
    } else if (!this.permissionAllCompanyEdit) {
      this.loadCompanyGroup(this.userServ.userStorage.idCompany);
      this.loadCompanyUsers(this.userServ.userStorage.idCompany);

      this.form.patchValue({
        company: this.userServ.userStorage.company,
        dateFrom: new Date(this.startDate.setHours(0, 0, 0, 0)),
        dateTo: new Date(this.endDate.setHours(23, 59, 0, 0)),
      });
      this.showLoader = false;

    } else {
      this.form.patchValue({
        company: this.allCompanies,
        dateFrom: new Date(this.startDate.setHours(0, 0, 0, 0)),
        dateTo: new Date(this.endDate.setHours(23, 59, 0, 0)),
      });
      this.showLoader = false;
    }
  }

  initForm() {
    this.form = new FormGroup(
      {
        company: new FormControl(null, {
          updateOn: 'blur',
          validators: [Validators.required],
        }),
        group: new FormControl(null, {
          updateOn: 'blur',
        }),
        user: new FormControl(null, {
          updateOn: 'blur',
        }),
        number: new FormControl(null, {
          updateOn: 'blur',
        }),
        dateFrom: new FormControl(null, {
          updateOn: 'blur',
          validators: [Validators.required],
        }),
        dateTo: new FormControl(null, {
          updateOn: 'blur',
          validators: [Validators.required],
        }),
      },
      customValidators.dateValidator('dateFrom', 'dateTo')
    );
  }

  onAssignCompanyOrEmployee() {
    if (!this.form.valid) {
      this.validationError = true;
      return;
    }
    this.savingAssignment = true;

    let spaceAssignment = new SpaceAssignmentModel();

    spaceAssignment.idSpace = Number(this.spaceId);
    spaceAssignment.dateFrom = this.startDate;
    spaceAssignment.dateTo = this.endDate;
    spaceAssignment.number = this.form.value.number;
    spaceAssignment.idGroup = this.form.value.group
      ? this.form.value.group.id
      : null;
    spaceAssignment.idCompany = this.form.value.company
      ? this.form.value.company.id
      : null;
    spaceAssignment.idUser =
      this.form.value.user?.id === null ? null : this.form.value.user;

    if (this.assignmentId) {
      this.spaceServ
        .updateAssignment(this.assignmentId, spaceAssignment)
        .subscribe(
          () => {
            this.savingAssignment = false;
            this.close();
          },
          (err) => {
            this.handleError(err.statusCode);
          }
        );
    } else {
      this.spaceServ.addAssignment(spaceAssignment).subscribe(() => {
        this.savingAssignment = false;
        this.close();
      });
    }
  }

  async handleError(errorCode: number) {
    let errorMessage: string;
    switch (errorCode) {
      case 403: {
        errorMessage = this.translateServ.instant('ERROR.HTTP_403');
        break;
      }
      case 401: {
        errorMessage = this.translateServ.instant('ERROR.HTTP_401');
        break;
      }
    }
    const alert = await this.alertController.create({
      message: errorMessage,
      buttons: ['OK'],
    });
    await alert.present();
  }

  onCompanyChange(event) {
    this.form.patchValue({
      user: this.allUsers.id,
      group: this.allGroups,
    });
    if (event && event?.length !== 0 && event?.id) {
      // reset użytkownika przy zmianie firmy

      this.loadCompanyGroup(event.id);
      this.loadCompanyUsers(event.id);
    } else {
      this.usersList = null;
      this.groupDropdownData = null;
      this.userSelect.ngOnInit();
    }
  }

  onGroupChange(event) {
    if (event) {
      this.form.patchValue({
        user: this.allUsers.id,
      });

      // TODO load
      this.loadCompanyUsers(this.form.value.company.id, event.id);
    }
  }

  onUserChange(event) {
    this.form.patchValue({
      number: event.carPlate,
      user: event.id,
    });
    if (event?.id == null || event?.length === 0) {
      this.isUserSelected = false;
      this.label = this.translateServ.instant('SELECT_USER');
    } else {
      this.isUserSelected = true;
      this.label = '';
    }
  }

  loadCompanyUsers(idCompany: number, idGroup?: number) {
    this.userServ
      .getUsersCompany({
        idCompany: idCompany,
        basicModel: 'true',
        idGroup: idGroup,
        active: 'true'
      })
      .subscribe((res) => {
        this.usersList = [this.allUsers, ...res];
        setTimeout(() => {
          this.userSelect.ngOnInit();
        });
      });
  }

  afterPickerClosed(date, startDate: string = 'start' || 'end') {
    if (startDate == 'start') {
      this.startDate = date;
      this.form.patchValue({
        dateFrom: date,
      });
    } else {
      this.endDate = date;
      this.form.patchValue({
        dateTo: date,
      });
    }
  }

  loadCompanyGroup(idCompany: number) {
    this.groupServ
      .getCompanyGroups(idCompany, CompanyGroupType.Parking)
      .subscribe((res: CompanyGroup[]) => {
        this.groupDropdownData = [this.allGroups, ...res];
      });
  }

  loadAssignmentDetails(assignment: SpaceAssignmentModel) {
    this.startDate = assignment.dateFrom;
    this.endDate = assignment.dateTo;

    this.form.patchValue({
      company: assignment.company ? assignment.company : this.dropdownData[0],
      group: assignment.group ? assignment.group : this.allGroups,
      user: assignment.user?.id,
      dateFrom: assignment.dateFrom,
      dateTo: assignment.dateTo,
      number: assignment.number,
    });
  }

  onSelectTouched(formControlName: string): void {
    this.form.get(formControlName).markAsTouched();
  }

  close() {
    this.modalController.dismiss();
  }
}
